import { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import api from "../lib/api";
import { toast } from "sonner";
import { Hash, Copy, ShieldCheck, UserCheck, Sparkles, Link as LinkIcon, Eye, Users } from "lucide-react";

/**
 * MyVCCard — members-only "Your VC#" widget.
 *
 * Shows the authenticated user their:
 *   • Personal VC# (their referral_token — share this to bring new members in)
 *   • Introducer's VC# + name (who brought them in)
 *   • One-click invite link with click-tracking
 *   • Live introducer-activity counters (7-day clicks + total introductees)
 *
 * Members never see anyone else's VC# beyond their own introducer.
 * The full master list is admin-only at /admin/vc-registry.
 */
const MyVCCard = () => {
  const [info, setInfo]     = useState(null);
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/me/vc-info").catch(() => ({ data: null })),
      api.get("/me/introducer-stats").catch(() => ({ data: null })),
    ]).then(([infoRes, statsRes]) => {
      if (!alive) return;
      setInfo(infoRes.data);
      setStats(statsRes.data);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const copy = (val, label) => {
    if (!val) return;
    try {
      navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch (_) {
      toast.error("Clipboard unavailable");
    }
  };

  const inviteLink = info?.my_vc
    ? `${window.location.origin}/register?vc=${encodeURIComponent(info.my_vc)}`
    : null;

  if (loading) {
    return (
      <Card className="bg-white border-slate-200" data-testid="my-vc-card-loading">
        <CardContent className="p-5">
          <div className="h-4 w-32 bg-slate-100 rounded animate-pulse mb-3" />
          <div className="h-6 w-48 bg-slate-100 rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }
  if (!info) return null;

  return (
    <Card className="bg-white border-slate-200" data-testid="my-vc-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hash size={14} className="text-blue-600" />
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">
              Your EchoSense VC#
            </p>
          </div>
          {info.is_accredited ? (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]">
              <Sparkles size={10} className="mr-1" /> Accredited
            </Badge>
          ) : (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[10px]">
              Not yet accredited
            </Badge>
          )}
        </div>

        {/* My VC# */}
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 flex items-center justify-between gap-3" data-testid="my-vc-row">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">My VC#</p>
            <p className="font-mono text-base font-semibold text-slate-900 truncate" data-testid="my-vc-value">
              {info.my_vc || "—"}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => copy(info.my_vc, "Your VC#")}
            className="border-blue-200 text-blue-700 hover:bg-blue-100 shrink-0"
            data-testid="copy-my-vc-btn"
          >
            <Copy size={12} className="mr-1.5" /> Copy
          </Button>
        </div>

        {/* Introducer */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="my-introducer-row">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
            <UserCheck size={11} /> Introduced By
          </p>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate" data-testid="my-introducer-name">
                {info.introducer_name || "—"}
              </p>
              <p className="font-mono text-xs text-slate-600 truncate" data-testid="my-introducer-vc">
                {info.introducer_vc || "—"}
              </p>
            </div>
            {info.joined_via_corp && (
              <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[10px] shrink-0">
                <ShieldCheck size={10} className="mr-1" /> Corporate
              </Badge>
            )}
          </div>
        </div>

        {/* One-click invite link */}
        {inviteLink && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="invite-link-row">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <LinkIcon size={11} /> One-Click Invite Link
            </p>
            <p className="font-mono text-[11px] text-slate-700 truncate" data-testid="invite-link-value">
              {inviteLink}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copy(inviteLink, "Invite link")}
              className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              data-testid="copy-invite-link-btn"
            >
              <Copy size={12} className="mr-1.5" /> Copy invite link
            </Button>
          </div>
        )}

        {/* Introducer activity counter */}
        {stats && (stats.clicks_total > 0 || stats.introductees_total > 0) && (
          <div className="grid grid-cols-3 gap-2" data-testid="introducer-stats">
            <div className="rounded-md border border-slate-200 bg-white p-2.5">
              <p className="text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <Eye size={9} /> Clicks · 7d
              </p>
              <p className="text-lg font-mono font-semibold text-slate-900 mt-0.5" data-testid="stats-clicks-7d">
                {stats.clicks_7d ?? 0}
              </p>
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-2.5">
              <p className="text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <Eye size={9} /> Clicks · all
              </p>
              <p className="text-lg font-mono font-semibold text-slate-900 mt-0.5" data-testid="stats-clicks-total">
                {stats.clicks_total ?? 0}
              </p>
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-2.5">
              <p className="text-[9px] uppercase tracking-widest text-emerald-700 flex items-center gap-1">
                <Users size={9} /> Members
              </p>
              <p className="text-lg font-mono font-semibold text-slate-900 mt-0.5" data-testid="stats-introductees">
                {stats.introductees_total ?? 0}
                <span className="text-[10px] text-slate-500 ml-1 font-sans">
                  ({stats.introductees_approved ?? 0} accr.)
                </span>
              </p>
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-500 leading-relaxed">
          Share your <strong>VC#</strong> or your <strong>invite link</strong> with anyone you'd
          like to introduce to the EchoSense Capital Club. The link pre-fills your VC# on the
          registration form — new members must still complete intake, KYC, and accredited review.
        </p>
      </CardContent>
    </Card>
  );
};

export default MyVCCard;
